import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import defaultAnonymousForm from '../../../api/data-dummy/default-anonymous-form.json';
import useR05Api from '../../../api/useR05Api';
import Header from '../../../components/header/Header';
import UserInfoForm from '../../../components/UserInfoForm/UserInfoForm';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import FujiModal from '../../../components/fuji-modal/FujiModal';
import { isReserveTicket, showNotificationDialog } from '../../../utils';
import { localStorageKeys, VOUCHER_TYPE } from '../../../utils/Constants';
import { useReservationFlow } from '../../../hooks/useReservationFlow';

import styles from './styles.module.css';

const BuyVoucherScreen = () => {
    const [loading, setLoading] = useState(false);
    const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
    const [warningMessenger, setWarningMessenger] = useState('');
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { validateVoucherCode } = useR05Api();
    const { next, back, isEdit, ticketInfo, localReservation } = useReservationFlow();
    const [searchParams] = useSearchParams();
    const appConfig = JSON.parse(localStorage.getItem('APP_CONFIG') ?? '{}');
    const anonymousForm = appConfig?.anonymousForm ?? [];
    const visibleOptionalForm = anonymousForm.filter((field: any) => field.isVisible);
    const combined: any = {};
    if (ticketInfo.voucherType === VOUCHER_TYPE.FIXED_FIRST || ticketInfo.voucherType === VOUCHER_TYPE.ONE_TIME_FIRST) {
        defaultAnonymousForm?.forEach((item: any) => {
            combined[item.key] = item;
        });
    }

    if (ticketInfo.isDisplayAnonymousForm) {
        visibleOptionalForm?.forEach((item: any) => {
            combined[item.key] = item;
        });
    }
    const optionalForm = Object.values(combined);

    const initialFormValues = useMemo(() => {
        const savedFormData = JSON.parse(localStorage.getItem(localStorageKeys.ANONYMOUS_FORM_ANSWERS) ?? '{}');
        const voucherCode = searchParams.get('voucherCode') ?? '';

        //クエリパラメータでvoucherCodeが指定された場合は、キャッシュより優先して入力する
        return voucherCode ? { ...savedFormData, voucherCode } : savedFormData;
    }, []);

    const handleFormSubmit = async (form: any) => {
        try {
            const { voucherCode, ...rest } = form;
            const anonymousFormAnswers = Object.fromEntries(
                Object.entries(rest)
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    .filter(([key, value]) => value !== null && value !== undefined)
                    .map(([key, value]) => [key, String(value)]),
            );
            if (
                ticketInfo.voucherType === VOUCHER_TYPE.FIXED_FIRST ||
                ticketInfo.voucherType === VOUCHER_TYPE.ONE_TIME_FIRST
            ) {
                setLoading(true);
                const response = await validateVoucherCode({
                    salesTicketId: ticketInfo.salesTicketId,
                    voucherCode: voucherCode,
                });
                setLoading(false);
                if (!response) {
                    return;
                }
            }
            localStorage.setItem(localStorageKeys.ANONYMOUS_FORM_ANSWERS, JSON.stringify(form ?? {}));
            if (!isReserveTicket(ticketInfo)) {
                navigate(`/buy/ticket?salesTicketId=${ticketInfo.salesTicketId}`, {
                    state: { anonymousFormAnswers: { ...anonymousFormAnswers, voucherCode } },
                });
            } else {
                const reservationInfoData = {
                    ...localReservation,
                    anonymousFormAnswers: { ...anonymousFormAnswers, voucherCode },
                };
                localStorage.setItem(localStorageKeys.RESERVATION_INFO_DATA, JSON.stringify(reservationInfoData));
                next('vouchers');
            }
        } catch (error: any) {
            setLoading(false);
            showNotificationDialog('error', '', t('notification.title'), t('error-message.voucher-code.wrong-code'));
            switch (error.errorType) {
                case 'not_match_voucher_code':
                case 'disabled_voucher_code':
                    throw error;
                case 'already_used_voucher_code':
                    setIsWarningModalOpen(true);
                    setWarningMessenger(t('error-message.voucher-code.already-used') ?? '');
                    break;
                case 'processing_voucher_code':
                    setIsWarningModalOpen(true);
                    setWarningMessenger(t('error-message.voucher-code.processing') ?? '');
                    break;
                default:
                    break;
            }
        }
    };

    return (
        <>
            {loading && <CenterLoading overFooter />}
            <FujiModal
                isOpen={isWarningModalOpen}
                onSubmit={() => {
                    setIsWarningModalOpen(false);
                }}
                txtSubmit={t('modal.warning-confirm')}
                body={warningMessenger}
            />
            <Header title={!isEdit ? t('header.buy-ticket') : t('reservation.header')} callback={back} />
            <div className="content-container scrollable">
                {/* 個人情報入力 */}
                {/* <p className={styles.screenName}>{t('reservation.input-user-info')}</p> */}
                <div className={styles.userInfoForm}>
                    <UserInfoForm
                        formFields={optionalForm}
                        onFormSubmit={handleFormSubmit}
                        initialValues={initialFormValues}
                    />
                </div>
            </div>
        </>
    );
};

export default BuyVoucherScreen;
